import React, { useState, useEffect } from 'react';
import { useAppLayout } from '../components/AppLayout';
import api from '../services/api';
import './UsersPage.css';

const ROLE_OPTIONS = [
  {
    value: 'salon-staff',
    label: 'Βασικός υπάλληλος',
    desc:  'Μόνο Ημερολόγιο',
    icon:  '👤',
    color: '#6B7280',
  },
  {
    value: 'salon-bookings',
    label: 'Υπάλληλος κρατήσεων',
    desc:  'Ημερολόγιο + Κρατήσεις',
    icon:  '📋',
    color: '#3B82F6',
  },
  {
    value: 'salon-clients',
    label: 'Υπάλληλος πελατών',
    desc:  'Ημερολόγιο + Κρατήσεις + Πελάτες',
    icon:  '👥',
    color: '#8B5CF6',
  },
];

const ROLE_LABEL = {
  'salon-staff':    { label: 'Βασικός',   color: '#6B7280', bg: '#F3F4F6' },
  'salon-bookings': { label: 'Κρατήσεις', color: '#1D4ED8', bg: '#EFF6FF' },
  'salon-clients':  { label: 'Πελάτες',   color: '#6D28D9', bg: '#EDE9FE' },
  'salon-admin':    { label: 'Admin',      color: '#065F46', bg: '#ECFDF5' },
};

export default function UsersPage() {
  const { openSidebar } = useAppLayout();
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser]   = useState(null);  // για αλλαγή role
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const [form, setForm] = useState({
    firstName: '',
    lastName:  '',
    email:     '',
    password:  '',
    role:      'salon-staff',
  });

  const loadUsers = () => {
    setLoading(true);
    api.get('/admin/users')
      .then(r => setUsers(r.data.users || []))
      .catch(() => setError('Αδυναμία φόρτωσης χρηστών'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadUsers(); }, []);

  const resetForm = () => setForm({ firstName: '', lastName: '', email: '', password: '', role: 'salon-staff' });

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await api.post('/admin/users', form);
      setShowModal(false);
      resetForm();
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error === 'Email already exists'
        ? 'Το email χρησιμοποιείται ήδη'
        : 'Σφάλμα κατά τη δημιουργία');
    } finally { setSaving(false); }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role });
      setUsers(prev => prev.map(u => u.id === userId
        ? { ...u, roles: [role] } : u
      ));
      setEditUser(null);
    } catch {
      setError('Σφάλμα αλλαγής δικαιωμάτων');
    }
  };

  const handleToggle = async (userId, currentEnabled) => {
    try {
      await api.put(`/admin/users/${userId}/toggle`);
      setUsers(prev => prev.map(u => u.id === userId
        ? { ...u, enabled: !currentEnabled } : u
      ));
    } catch {
      setError('Σφάλμα αλλαγής κατάστασης');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      setConfirmDelete(null);
    } catch {
      setError('Σφάλμα διαγραφής χρήστη');
    }
  };

  const userRole = (u) => u.roles?.find(r => r.startsWith('salon-')) || 'salon-staff';

  return (
    <div className="up-page">
      {/* ── Header ── */}
      <div className="up-header">
        <div className="up-header-top">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="hamburger-btn" onClick={openSidebar}>☰</button>
            <h1 className="up-title">👤 Χρήστες</h1>
          </div>
          <button className="up-add-btn" onClick={() => { resetForm(); setShowModal(true); setError(''); }}>
            + Νέος χρήστης
          </button>
        </div>
      </div>

      {/* ── Role legend ── */}
      <div className="up-legend">
        {ROLE_OPTIONS.map(r => (
          <div key={r.value} className="up-legend-item">
            <span className="up-legend-icon">{r.icon}</span>
            <div>
              <div className="up-legend-label">{r.label}</div>
              <div className="up-legend-desc">{r.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="up-content">
        {error && <div className="up-error">{error} <button onClick={() => setError('')}>✕</button></div>}

        {loading && <div className="up-empty"><div className="up-spinner" /><p>Φόρτωση...</p></div>}

        {!loading && users.length === 0 && (
          <div className="up-empty">
            <div className="up-empty-icon">👤</div>
            <p className="up-empty-title">Δεν υπάρχουν χρήστες ακόμα</p>
            <p className="up-empty-sub">Πρόσθεσε τον πρώτο υπάλληλο</p>
          </div>
        )}

        {!loading && users.map(u => {
          const role     = userRole(u);
          const roleMeta = ROLE_LABEL[role] || ROLE_LABEL['salon-staff'];
          const isAdmin  = role === 'salon-admin';

          return (
            <div key={u.id} className={'up-card' + (!u.enabled ? ' up-card-disabled' : '')}>
              <div className="up-card-left">
                <div className="up-avatar" style={{ background: roleMeta.bg, color: roleMeta.color }}>
                  {(u.firstName || u.email)?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="up-card-name">
                    {u.firstName} {u.lastName}
                    {!u.enabled && <span className="up-disabled-badge">Ανενεργός</span>}
                  </div>
                  <div className="up-card-email">{u.email}</div>
                  <span className="up-role-badge" style={{ color: roleMeta.color, background: roleMeta.bg }}>
                    {roleMeta.label}
                  </span>
                </div>
              </div>

              {/* Permissions chips */}
              <div className="up-perms">
                {['calendar','bookings','clients','services','stats','settings'].map(p => (
                  <span key={p} className={'up-perm' + (u.permissions?.[p] ? ' up-perm-on' : '')}>
                    {p === 'calendar'  ? '📅' :
                     p === 'bookings'  ? '📋' :
                     p === 'clients'   ? '👥' :
                     p === 'services'  ? '✂️' :
                     p === 'stats'     ? '📊' : '⚙️'}
                  </span>
                ))}
              </div>

              {/* Actions */}
              {!isAdmin && (
                <div className="up-actions">
                  {/* Αλλαγή role */}
                  {editUser === u.id ? (
                    <div className="up-role-picker">
                      {ROLE_OPTIONS.map(r => (
                        <button key={r.value}
                          className={'up-role-opt' + (role === r.value ? ' active' : '')}
                          onClick={() => handleRoleChange(u.id, r.value)}>
                          {r.icon} {r.label}
                        </button>
                      ))}
                      <button className="up-role-cancel" onClick={() => setEditUser(null)}>Ακύρωση</button>
                    </div>
                  ) : (
                    <button className="up-btn up-btn-edit" onClick={() => setEditUser(u.id)}>
                      ✏️ Δικαιώματα
                    </button>
                  )}

                  <button
                    className={'up-btn ' + (u.enabled ? 'up-btn-warn' : 'up-btn-success')}
                    onClick={() => handleToggle(u.id, u.enabled)}>
                    {u.enabled ? '⏸ Απενεργοποίηση' : '▶ Ενεργοποίηση'}
                  </button>

                  {confirmDelete === u.id ? (
                    <>
                      <button className="up-btn up-btn-danger" onClick={() => handleDelete(u.id)}>
                        ⚠️ Επιβεβαίωση
                      </button>
                      <button className="up-btn up-btn-secondary" onClick={() => setConfirmDelete(null)}>
                        Ακύρωση
                      </button>
                    </>
                  ) : (
                    <button className="up-btn up-btn-danger" onClick={() => setConfirmDelete(u.id)}>
                      🗑️ Διαγραφή
                    </button>
                  )}
                </div>
              )}
              {isAdmin && <div className="up-admin-note">👑 Διαχειριστής — δεν επεξεργάζεται</div>}
            </div>
          );
        })}
      </div>

      {/* ── Modal: Νέος χρήστης ── */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <span className="modal-title">👤 Νέος χρήστης</span>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreate} style={{ display: 'contents' }}>
              <div className="modal-body">
                {error && <div className="up-error" style={{ marginBottom: 12 }}>{error}</div>}

                <div className="field">
                  <label>Όνομα *</label>
                  <input type="text" placeholder="π.χ. Μαρία" required
                    value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
                </div>

                <div className="field">
                  <label>Επώνυμο</label>
                  <input type="text" placeholder="π.χ. Παπαδοπούλου"
                    value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
                </div>

                <div className="field">
                  <label>Email *</label>
                  <input type="email" placeholder="maria@example.com" required
                    value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>

                <div className="field">
                  <label>Κωδικός πρόσβασης *</label>
                  <input type="password" placeholder="Τουλάχιστον 6 χαρακτήρες" required minLength={6}
                    value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                </div>

                <div className="field">
                  <label>Δικαιώματα *</label>
                  <div className="up-role-cards">
                    {ROLE_OPTIONS.map(r => (
                      <div key={r.value}
                        className={'up-role-card' + (form.role === r.value ? ' active' : '')}
                        onClick={() => setForm(f => ({ ...f, role: r.value }))}>
                        <span className="up-role-card-icon">{r.icon}</span>
                        <div>
                          <div className="up-role-card-label">{r.label}</div>
                          <div className="up-role-card-desc">{r.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Ακύρωση
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Δημιουργία...' : '+ Δημιουργία χρήστη'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}